import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import CategoriesSlider from '../../components/categories-slider/CategoriesSlider'
import CategoryCountdown from '../../components/categories-slider/CategoryCountdown'
import MenuItem from '../../components/menu-item/MenuItem'
import { useGetCategoriesQuery } from '../../store/api/categoriesApi'
import { useGetProductsQuery } from '../../store/api/productsApi'
import { useOutletContext } from 'react-router-dom'
import Loading from '../../assets/icons/loading.svg?react'
import { useTranslation } from 'react-i18next'
import DaysOffModal from './DaysOffModal/DaysOffModal'
import './Home.scss'
import AnnouncementModal from './AnnouncementModal/AnnouncementModal'
import { IProduct } from '../../types/products'
import { IProductGroup } from '../../types/app'
import { getStorageKey } from '../../utils/appType'

const c = 'home-page'

const Home = () => {
  const [openModal, setOpenModal] = useState(false)
  const [openAnnouncement, setOpenAnnouncement] = useState(false)
  const [showScrollTop, setShowScrollTop] = useState(false)

  useEffect(() => {
    const onScroll = () => setShowScrollTop(window.scrollY > 300)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])
  const hasRestoredScroll = useRef(false)
  const { cart, setCart, searchValue } = useOutletContext<{
    cart: IProduct[]
    setCart: React.Dispatch<React.SetStateAction<IProduct[]>>
    searchValue: string
  }>()
  const generalInfo = (() => {
    try {
      return JSON.parse(localStorage.getItem(getStorageKey('info')) || '{}')
    } catch {
      return {}
    }
  })()
  const { t, i18n } = useTranslation()

  const { data: categories = [] } = useGetCategoriesQuery()
  const { data: products = [], isLoading: loadingProducts } =
    useGetProductsQuery(undefined)

  const getTranslatedName = (item: IProductGroup) => {
    if (i18n.language === 'ru' && item?.category_name_ru) {
      return item?.category_name_ru
    }

    return item?.category_name
  }

  useEffect(() => {
    if (!loadingProducts && !hasRestoredScroll.current) {
      hasRestoredScroll.current = true
      const savedScrollY = sessionStorage.getItem('homeScrollY')
      if (savedScrollY) {
        sessionStorage.removeItem('homeScrollY')
        window.scrollTo({ top: parseInt(savedScrollY, 10) })
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    }
  }, [loadingProducts])

  useEffect(() => {
    if (generalInfo?.dayOff) {
      setOpenModal(true)
    }

    if (generalInfo?.announcement) {
      setOpenAnnouncement(true)
    }
  }, [])

  const filteredProducts = useMemo(() => {
    if (searchValue === '') {
      return products
    }

    return products.map((product: IProductGroup) => {
      const updatedItems = product.items?.filter(
        (item: IProduct) =>
          (item.name &&
            item.name?.toLowerCase().includes(searchValue.toLowerCase())) ||
          (item.ru_name &&
            item.ru_name?.toLowerCase().includes(searchValue.toLowerCase())),
      )

      return {
        ...product,
        items: updatedItems,
      }
    })
  }, [searchValue, products])

  return (
    <div className={c}>
      <CategoriesSlider data={categories} />
      <div
        className={`${c}__menu-items${
          !filteredProducts ||
          filteredProducts.every((product) => product.items.length === 0) ||
          loadingProducts
            ? ' no-data'
            : ''
        }`}
      >
        {loadingProducts ? (
          <Loading />
        ) : filteredProducts &&
          filteredProducts.some((product) => product.items.length) ? (
          filteredProducts.map((product: IProductGroup, index) => {
            if (!product.items.length) {
              return null
            }

            return (
              <div
                key={index}
                id={`category-${product.category_id}`}
                className={`${c}__menu-items-container`}
              >
                {(() => {
                  const cat = categories.find(
                    (c) => c._id === product.category_id,
                  )
                  return (
                    <div className={`${c}__menu-items-container-title`}>
                      <span>{getTranslatedName(product)}</span>
                      {cat?.isNew && (
                        <span className={`${c}__new-badge`}>{t('NEW')}</span>
                      )}
                      {cat?.isNew && cat?.specialOfferEnd && (
                        <CategoryCountdown endDate={cat.specialOfferEnd} />
                      )}
                    </div>
                  )
                })()}
                <div className={`${c}__menu-items-container-products`}>
                  {product.items.map((item: IProduct, i: number) => {
                    return (
                      <MenuItem
                        key={i}
                        data={item}
                        cart={cart}
                        setCart={setCart}
                      />
                    )
                  })}
                </div>
              </div>
            )
          })
        ) : (
          <div className={`${c}__menu-items-no_data`}>
            Nu sunt produse pentru aceasta categorie la moment
          </div>
        )}
      </div>
      {openModal && (
        <DaysOffModal isOpen={openModal} setIsOpen={setOpenModal} />
      )}
      {openAnnouncement && (
        <AnnouncementModal
          isOpen={openAnnouncement}
          setIsOpen={setOpenAnnouncement}
        />
      )}
      {showScrollTop && (
        <button
          className={`${c}__scroll-top`}
          onClick={scrollToTop}
          aria-label='Scroll to top'
        >
          &#8679;
        </button>
      )}
    </div>
  )
}

export default Home
